import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Typography, Grid, Paper, Tab, Tabs } from '@mui/material';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import TableChartIcon from '@mui/icons-material/TableChart';
import PublicIcon from '@mui/icons-material/Public';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import AssignmentIcon from '@mui/icons-material/Assignment';
import HubIcon from '@mui/icons-material/Hub';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import MigrationQueue from './DivergenceDashboard/MigrationQueue';
import SyncHealth from '../SyncHealth/SyncHealth';
import Requests from './Requests/Requests';
import Integrations from './Integrations/Integrations';
import AccountFkSweep from './RegistryControlPlane/AccountFkSweep';
import StateDataConsole from './StateData/StateDataConsole';
import BrowserDeskConsole from './BrowserDesk/BrowserDeskConsole';
import './Implementations.css';

export const IMPL_CLIENTS = [
    { name: 'Stubby', icon: <RocketLaunchIcon sx={{ fontSize: 32, color: '#004df2' }} />, url: 'https://stubby.asknexus.ai' },
    { name: 'Eve',    icon: <AutoAwesomeIcon  sx={{ fontSize: 32, color: '#004df2' }} />, url: 'https://eve.asknexus.ai' },
];

const HWD_KEY = 'hwd-unlocked';
const STATE_DATA_TAB_INDEX = 6;
const DATASET_TAGS_TAB_INDEX = 7;
const BROWSER_DESK_TAB_INDEX = 8;
const HWD_TAB_INDEX = 9;   // 5 is Data Configuration and 7 Sync Staleness (both navigate away), 6 is State Data

const hostOf = (url) => {
    try { return new URL(url).host; } catch { return url; }
};

const ClientCard = ({ name, icon, url }) => (
    <Paper
        component="a"
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="impl-card d-hover"
        elevation={0}
    >
        <OpenInNewIcon className="impl-card-ext" sx={{ fontSize: 16 }} />
        <Box className="impl-card-body">
            <Box className="impl-card-icon">{icon}</Box>
            <Box>
                <Typography className="nexus-font md">{name}</Typography>
                <Typography className="d-font" sx={{ fontSize: 12, color: 'var(--color-text-secondary, #666)' }}>
                    {hostOf(url)}
                </Typography>
            </Box>
        </Box>
    </Paper>
);

const AppsTab = () => (
    <Grid container spacing={2}>
        {IMPL_CLIENTS.map((c) => (
            <Grid item xs={12} sm={6} md={4} key={c.name}>
                <ClientCard {...c} />
            </Grid>
        ))}
    </Grid>
);

// Easter-egg activation: the sync-health tab appears only after `howWeDoin()`
// is called from the browser console. The unlock persists across reloads via
// localStorage, and `lockHowWeDoin()` removes it.
function setupEasterEgg(setUnlocked) {
    if (typeof window === 'undefined') return;

    window.howWeDoin = () => {
        localStorage.setItem(HWD_KEY, '1');
        setUnlocked(true);
        // eslint-disable-next-line no-console
        console.log('%c🚀 sync health unlocked — head to /implementations',
            'color:#004df2;font-weight:600;font-size:13px');
        return 'unlocked';
    };
    window.lockHowWeDoin = () => {
        localStorage.removeItem(HWD_KEY);
        setUnlocked(false);
        return 'locked';
    };
}

const REQUESTS_TAB_INDEX = 2;

// Data Configuration is a whole area with its own routes (/data-config/:client/...)
// rather than a panel, so its tab OPENS it instead of rendering inside this page.
// Keeping the route intact is what lets a client or source be bookmarked or
// pasted into a ticket, which is most of the point of those addresses.
const DATA_CONFIG_TAB_INDEX = 5;

const Implementations = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    // ?request=<id> deep-links one success request from the work views. Landing
    // on the default Clients tab would have been a silent no-op — the link
    // worked, the route existed, and nothing about the request was on screen.
    const requestId = searchParams.get('request');
    const [unlocked, setUnlocked] = useState(() => localStorage.getItem(HWD_KEY) === '1');
    // ?tab=browser-desk opens the desk console directly (desk alert emails link here).
    const [tab, setTab] = useState(requestId ? REQUESTS_TAB_INDEX
        : searchParams.get('tab') === 'browser-desk' ? BROWSER_DESK_TAB_INDEX : 0);

    useEffect(() => {
        setupEasterEgg(setUnlocked);
    }, []);

    // If the user locks while sitting on the hidden tab, snap back.
    useEffect(() => {
        if (!unlocked && tab === HWD_TAB_INDEX) setTab(0);
    }, [unlocked, tab]);

    return (
        <Box className="implementations-container">
            <Box className="implementations-header">
                <Typography className="nexus-font lg" sx={{ mb: 0.5 }}>
                    Implementations
                </Typography>
                <Typography className="d-font md" sx={{ color: '#666' }}>
                    Manage and track client implementation projects.
                </Typography>
            </Box>

            <Tabs
                value={tab}
                onChange={(_, v) => {
                    if (v === DATA_CONFIG_TAB_INDEX) navigate('/data-config');
                    else if (v === DATASET_TAGS_TAB_INDEX) navigate('/implementations/dataset-tags');
                    else setTab(v);
                }}
                // Ten tabs no longer fit at laptop widths: scroll them with arrows instead of
                // clipping the last ones off the edge.
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                sx={{ mb: 3, borderBottom: 1, borderColor: 'var(--color-border)' }}
                TabIndicatorProps={{ style: { backgroundColor: 'var(--color-brand-blue, #004df2)' } }}
            >
                <Tab
                    label="Clients"
                    className="d-font"
                    icon={<RocketLaunchIcon sx={{ fontSize: 16 }} />}
                    iconPosition="start"
                    sx={{ textTransform: 'none', fontSize: 13, minHeight: 44 }}
                />
                <Tab
                    label="Migration Queue"
                    className="d-font"
                    icon={<TrackChangesIcon sx={{ fontSize: 16 }} />}
                    iconPosition="start"
                    sx={{ textTransform: 'none', fontSize: 13, minHeight: 44 }}
                />
                <Tab
                    label="Requests"
                    className="d-font"
                    icon={<AssignmentIcon sx={{ fontSize: 16 }} />}
                    iconPosition="start"
                    sx={{ textTransform: 'none', fontSize: 13, minHeight: 44 }}
                />
                <Tab
                    label="Integrations"
                    className="d-font"
                    icon={<HubIcon sx={{ fontSize: 16 }} />}
                    iconPosition="start"
                    sx={{ textTransform: 'none', fontSize: 13, minHeight: 44 }}
                />
                <Tab
                    label="FK Sweep"
                    className="d-font"
                    icon={<AccountTreeIcon sx={{ fontSize: 16 }} />}
                    iconPosition="start"
                    sx={{ textTransform: 'none', fontSize: 13, minHeight: 44 }}
                />
                <Tab
                    label="Data Configuration"
                    className="d-font"
                    icon={<TableChartIcon sx={{ fontSize: 16 }} />}
                    iconPosition="start"
                    sx={{ textTransform: 'none', fontSize: 13, minHeight: 44 }}
                />
                <Tab
                    label="State Data"
                    className="d-font"
                    icon={<PublicIcon sx={{ fontSize: 16 }} />}
                    iconPosition="start"
                    sx={{ textTransform: 'none', fontSize: 13, minHeight: 44 }}
                />
                <Tab
                    label="Sync Staleness"
                    className="d-font"
                    icon={<LocalOfferIcon sx={{ fontSize: 16 }} />}
                    iconPosition="start"
                    sx={{ textTransform: 'none', fontSize: 13, minHeight: 44 }}
                />
                <Tab
                    label="Browser Desk"
                    className="d-font"
                    icon={<TravelExploreIcon sx={{ fontSize: 16 }} />}
                    iconPosition="start"
                    sx={{ textTransform: 'none', fontSize: 13, minHeight: 44 }}
                />
                {unlocked && (
                    <Tab
                        label="How We Doin'"
                        className="d-font"
                        icon={<MonitorHeartIcon sx={{ fontSize: 16 }} />}
                        iconPosition="start"
                        sx={{ textTransform: 'none', fontSize: 13, minHeight: 44 }}
                    />
                )}
            </Tabs>

            {tab === 0 && <AppsTab />}
            {tab === 1 && <MigrationQueue />}
            {tab === 2 && <Requests openRequestId={requestId} />}
            {tab === 3 && <Integrations />}
            {tab === 4 && <AccountFkSweep />}
            {tab === STATE_DATA_TAB_INDEX && <StateDataConsole />}
            {tab === BROWSER_DESK_TAB_INDEX && <BrowserDeskConsole />}
            {tab === HWD_TAB_INDEX && unlocked && <SyncHealth />}
        </Box>
    );
};

export default Implementations;
